import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  List, X, User, SignOut, House, GraduationCap, BookOpen, Gear, Atom, Flask, Calculator, CurrencyEur,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import Logo from "@/components/academy/Logo";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data);
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post("/auth/register", data);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/" />;
  }

  return children;
};

// Header
const Header = () => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Start", icon: House },
    { path: "/kurse", label: "Kurse", icon: GraduationCap },
    { path: "/tools", label: "Tools", icon: Calculator },
    { path: "/wissen", label: "Wissen", icon: Atom },
    { path: "/chemikalien", label: "Chemikalien", icon: Flask },
    { path: "/glossar", label: "Glossar", icon: BookOpen },
    { path: "/preise", label: "Preise", icon: CurrencyEur },
  ];

  const isActive = (path) => (path === "/" ? location.pathname === "/" : location.pathname.startsWith(path));

  const handleLogout = async () => {
    await logout();
    toast.success("Erfolgreich abgemeldet");
    navigate("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          <Logo size={34} />

          <nav className="hidden lg:flex items-center gap-5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium uppercase tracking-wider ${isActive(link.path) ? "active" : "text-[var(--da-text)]"}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" className="btn-secondary text-sm py-2 px-4" data-testid="admin-dashboard-btn">
                    Admin
                  </Link>
                )}
                <Link to="/profil" className="flex items-center gap-2 text-[var(--da-text)] hover:text-[var(--da-primary)] transition-colors" data-testid="profile-link">
                  <User size={20} weight="duotone" />
                  <span className="text-sm">{user.name}</span>
                </Link>
                <button onClick={handleLogout} className="text-muted-custom hover:text-[var(--da-text)] transition-colors" data-testid="logout-btn">
                  <SignOut size={20} weight="duotone" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm text-muted-custom hover:text-[var(--da-text)]" data-testid="login-link">
                  Anmelden
                </Link>
                <Link to="/register" className="btn-primary text-sm py-2 px-6" data-testid="register-btn">
                  Registrieren
                </Link>
              </>
            )}
          </div>

          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden text-[var(--da-text)] p-2" data-testid="mobile-menu-btn">
            {menuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-[var(--da-surface)] border-t border-custom">
          <nav className="container-custom py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 py-3 px-4 rounded ${isActive(link.path) ? "bg-[var(--da-primary)]/10 text-[var(--da-primary)]" : "text-[var(--da-text)] hover:bg-white/5"}`}
              >
                <link.icon size={20} weight="duotone" />
                {link.label}
              </Link>
            ))}
            <div className="border-t border-custom mt-2 pt-2">
              {user ? (
                <>
                  <Link to="/profil" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 py-3 px-4 text-[var(--da-text)] hover:bg-white/5 rounded">
                    <User size={20} weight="duotone" /> Mein Profil
                  </Link>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 py-3 px-4 text-[var(--da-text)] hover:bg-white/5 rounded">
                      <Gear size={20} weight="duotone" /> Admin Panel
                    </Link>
                  )}
                  <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="flex items-center gap-3 py-3 px-4 text-muted-custom hover:bg-white/5 rounded w-full">
                    <SignOut size={20} weight="duotone" /> Abmelden
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 py-3 px-4 text-[var(--da-text)] hover:bg-white/5 rounded">
                    <User size={20} weight="duotone" /> Anmelden
                  </Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 py-3 px-4 bg-[var(--da-primary)] text-[#04222a] rounded font-semibold">
                    Registrieren
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[var(--da-surface)] border-t border-custom">
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <Logo size={30} />
            <p className="text-muted-custom text-sm leading-relaxed mt-6">
              Die Fachacademy von OneWorld für professionelle Fahrzeugaufbereitung – mit kostenlosen
              Einsteigerkursen und vertieftem Profi-Wissen.
            </p>
          </div>
          <div>
            <h4 className="text-[var(--da-text)] font-semibold mb-6 text-sm uppercase tracking-wider">Lernen</h4>
            <div className="space-y-3 text-sm">
              <Link to="/kurse" className="block text-muted-custom hover:text-[var(--da-primary)] transition-colors">Kurse</Link>
              <Link to="/tools" className="block text-muted-custom hover:text-[var(--da-primary)] transition-colors">Werkstatt-Tools</Link>
              <Link to="/preise" className="block text-muted-custom hover:text-[var(--da-primary)] transition-colors">Preise &amp; Zugänge</Link>
              <Link to="/wissen" className="block text-muted-custom hover:text-[var(--da-primary)] transition-colors">Wissensdatenbank</Link>
              <Link to="/chemikalien" className="block text-muted-custom hover:text-[var(--da-primary)] transition-colors">Chemikalienkatalog</Link>
              <Link to="/glossar" className="block text-muted-custom hover:text-[var(--da-primary)] transition-colors">Glossar</Link>
              <Link to="/register" className="block text-muted-custom hover:text-[var(--da-primary)] transition-colors">Registrieren</Link>
            </div>
          </div>
          <div>
            <h4 className="text-[var(--da-text)] font-semibold mb-6 text-sm uppercase tracking-wider">Rechtliches</h4>
            <div className="space-y-3 text-sm">
              <Link to="/impressum" className="block text-muted-custom hover:text-[var(--da-primary)] transition-colors">Impressum</Link>
              <Link to="/datenschutz" className="block text-muted-custom hover:text-[var(--da-primary)] transition-colors">Datenschutz</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-custom mt-12 pt-8 text-center">
          <p className="text-muted-custom text-sm">© {new Date().getFullYear()} OneWorld DetailAcademy. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </footer>
  );
};

// Lazy-loaded pages
const LandingPage = React.lazy(() => import("./pages/academy/LandingPage"));
const CoursesPage = React.lazy(() => import("./pages/academy/CoursesPage"));
const CourseDetailPage = React.lazy(() => import("./pages/academy/CourseDetailPage"));
const LessonPage = React.lazy(() => import("./pages/academy/LessonPage"));
const GlossaryPage = React.lazy(() => import("./pages/academy/GlossaryPage"));
const KnowledgeBasePage = React.lazy(() => import("./pages/academy/KnowledgeBasePage"));
const KBArticlePage = React.lazy(() => import("./pages/academy/KBArticlePage"));
const ChemicalCatalogPage = React.lazy(() => import("./pages/academy/ChemicalCatalogPage"));
const ChemicalDetailPage = React.lazy(() => import("./pages/academy/ChemicalDetailPage"));
const PricingPage = React.lazy(() => import("./pages/academy/PricingPage"));
const ToolsPage = React.lazy(() => import("./pages/academy/ToolsPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const AdminPage = React.lazy(() => import("./pages/AdminPage"));
const ImpressumPage = React.lazy(() => import("./pages/ImpressumPage"));
const DatenschutzPage = React.lazy(() => import("./pages/DatenschutzPage"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="spinner mx-auto mb-4"></div>
      <p className="text-muted-custom">Laden...</p>
    </div>
  </div>
);

const Layout = ({ children }) => (
  <>
    <Header />
    <main className="pt-20">{children}</main>
    <Footer />
  </>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: 'var(--da-surface)',
              color: 'var(--da-text)',
              border: '1px solid var(--da-border)',
            },
          }}
        />
        <React.Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Layout><LandingPage /></Layout>} />
            <Route path="/kurse" element={<Layout><CoursesPage /></Layout>} />
            <Route path="/kurse/:slug" element={<Layout><CourseDetailPage /></Layout>} />
            <Route path="/kurse/:slug/:lessonSlug" element={<Layout><LessonPage /></Layout>} />
            <Route path="/glossar" element={<Layout><GlossaryPage /></Layout>} />
            <Route path="/wissen" element={<Layout><KnowledgeBasePage /></Layout>} />
            <Route path="/wissen/:slug" element={<Layout><KBArticlePage /></Layout>} />
            <Route path="/chemikalien" element={<Layout><ChemicalCatalogPage /></Layout>} />
            <Route path="/chemikalien/:slug" element={<Layout><ChemicalDetailPage /></Layout>} />
            <Route path="/preise" element={<Layout><PricingPage /></Layout>} />
            <Route path="/tools" element={<Layout><ToolsPage /></Layout>} />
            <Route path="/login" element={<Layout><LoginPage /></Layout>} />
            <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
            <Route path="/impressum" element={<Layout><ImpressumPage /></Layout>} />
            <Route path="/datenschutz" element={<Layout><DatenschutzPage /></Layout>} />
            <Route
              path="/profil"
              element={
                <ProtectedRoute>
                  <Layout><ProfilePage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
